/* eslint-disable no-unused-vars */

import React, { memo, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useState } from 'react';
import { store } from 'redux/store';

import PermissionRestrict from 'hoc/permission-restrict';
import AppBar from '@components/app-bar';
import ButtonStatus from '@components/button-status/button-status.component';
import Icon from '@components/icon-component';
import LoadingRound from '@components/loading-round/loading-round';

import { getAppDispatch } from '@utilities/dispatch.util';
import { filterActions } from '@redux/slices/filters';
import { PERMISSIONS } from 'permissions/constants';
import { decodeToken } from '@utilities/authentication';
import { initWebWorker } from 'webworker';

import './task-type.scss';
import style from './style.module.scss';

const TaskType = () => {
  const navigate = useNavigate();
  const [view, setView] = useState('');
  const [allTasksData, setAllTasksData] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    initWebWorker({
      args: {
        store: { ...store.getState() },
        permissions: decodeToken()?.permissions || {}
      },
      type: 'get-task-type-records'
    })
      .then((data) => {
        setAllTasksData(data?.result);
        setIsLoading(false);
      })
      .catch((error) => {
        setIsLoading(false);
      });
  }, []);

  const {
    // checks
    IsCheckOverDue = false,
    allCheckTasksCount = 0,
    allDoneCheckTasksCount = 0,
    // inspection,
    allInspectionTasksCount = 0,
    IsInspectionOverDue = false,
    allDoneInspectionTasksCount = 0,

    allWOTasks = [],
    allDoneWOTasks = [],
    allInspectionTasks = [],
    allDoneInspectionTasks = [],
    allTasks = [],
    allDoneTasks = [],
    isOverDue = false,
    isWOOverDue = false,
    allHseEvents,
    newWorkRequestList = [],
    newWorkOrderList = [],
    editEquipmentCount = [],
    editEquipmentViewBySitesCount = 0,

    allWOTasksCount,
    allDoneWOTasksCOunt,
    newWorkOrderListCOunt,
    newWorkRequestListCOunt,
    allHseEventsCount
  } = allTasksData;

  const dispatch = getAppDispatch?.();
  const fallback = () => {
    return <></>;
  };

  const pageHeader = () => {
    return (
      <div
        className="op-page-name op-listing-name px-4 md:px-8 py-7 flex flex-wrap justify-between items-center relative"
        style={{
          position: 'sticky',
          top: 0
        }}
      >
        <Link className="op-back " to={view ? '' : -1} onClick={() => view && setView('')}></Link>
        <h1 className="h1-semibold pl-16">Select {view ? 'view' : 'task type'}</h1>
        <div></div>
      </div>
    );
  };

  const permissions = decodeToken()?.permissions || {};

  const availableTaskType = [
    ...(permissions[PERMISSIONS.MOBILE_ONDEMAND_CHECKS]
      ? [
          {
            title: 'Checks',
            permissions: PERMISSIONS.MOBILE_ONDEMAND_CHECKS,
            icon: 'check-grey',
            onClick: () => setView('Check'),
            allTasks: allCheckTasksCount,
            doneTasks: allDoneCheckTasksCount,
            chipText: `${allDoneCheckTasksCount}/${allCheckTasksCount}`,
            isOverDue: IsCheckOverDue,
            dot: !!IsCheckOverDue && allDoneCheckTasksCount !== allCheckTasksCount
          }
        ]
      : []),

    ...(permissions[PERMISSIONS.MOBILE_ASSIGNED_INSPECTIONS]
      ? [
          {
            title: 'Inspections',
            permissions: PERMISSIONS.MOBILE_ASSIGNED_INSPECTIONS,
            icon: 'ins',
            onClick: () => setView('Inspection'),
            allTasks: allInspectionTasksCount,
            doneTasks: allDoneInspectionTasksCount,
            chipText: `${allDoneInspectionTasksCount}/${allInspectionTasksCount}`,
            isOverDue: IsInspectionOverDue,
            dot: !!IsInspectionOverDue && allDoneInspectionTasksCount !== allInspectionTasksCount
          }
        ]
      : []),

    ...(permissions[PERMISSIONS.MOBILE_ASSIGNED_WORKORDERS]
      ? [
          {
            title: 'Work Orders',
            permissions: PERMISSIONS.MOBILE_ASSIGNED_WORKORDERS,
            icon: 'work-orders',
            onClick: () => setView('Work Order'),
            allTasks: allWOTasksCount,
            doneTasks: allDoneWOTasksCOunt,
            chipText: `${allDoneWOTasksCOunt}/${allWOTasksCount}`,
            isOverDue: isWOOverDue,
            dot: !!isWOOverDue && allDoneWOTasksCOunt !== allWOTasksCount,
            recordCount: newWorkOrderListCOunt
          }
        ]
      : []),

    ...(permissions[PERMISSIONS.MOBILE_CREATE_WORKREQUEST]
      ? [
          {
            title: 'Work requests',
            permissions: PERMISSIONS.MOBILE_CREATE_WORKREQUEST,
            icon: 'work-orders',
            onClick: () => setView('Work Request'),
            allTasks: newWorkRequestListCOunt,
            doneTasks: [],
            chipText: newWorkRequestListCOunt,
            isOverDue: false,
            dot: false,
            recordCount: newWorkRequestListCOunt,
            notShowInProgressCount: true
          }
        ]
      : []),

    ...(permissions[PERMISSIONS.MOBILE_CREATE_HSE]
      ? [
          {
            title: 'HSE events',
            permissions: PERMISSIONS.MOBILE_CREATE_HSE,
            icon: 'hse',
            onClick: () => setView('HseEvent'),
            allTasks: allHseEventsCount,
            doneTasks: [],
            chipText: allHseEventsCount,
            isOverDue: false,
            dot: false,
            recordCount: allHseEventsCount,
            notShowInProgressCount: true
          }
        ]
      : []),

    ...(permissions[PERMISSIONS.EQUIPMENT_EDIT] || permissions[PERMISSIONS.EQUIPMENT_CREATE]
      ? [
          {
            title: 'New or edited assets',
            permissions: PERMISSIONS.EQUIPMENT_EDIT,
            icon: 'new-or-edit-assets-icon',
            onClick: () => setView('NewOrEditAsset'),
            allTasks: editEquipmentCount,
            doneTasks: [],
            chipText: editEquipmentCount,
            isOverDue: false,
            dot: false,
            recordCount: editEquipmentCount,
            notShowInProgressCount: true
          }
        ]
      : [])
  ];

  const clearAllFilterState = () => {
    dispatch?.(filterActions.setTaskFilters(null));
    dispatch?.(filterActions.setSitesFilters(null));
    dispatch?.(filterActions.setEquipmentsFilters(null));
  };

  const viewAllTasksEvent = () => {
    clearAllFilterState();
    navigate(
      `/task-records?type=${
        view === 'HseEvent' ? 'hseEvents' : view === 'NewOrEditAsset' ? 'newOrEditAssets' : view
      }&filterType=${
        view === 'HseEvent'
          ? 'hseEvents'
          : view === 'NewOrEditAsset'
          ? 'newOrEditAssets'
          : view === 'Inspection'
          ? 'inspection'
          : view === 'Check'
          ? 'check'
          : view === 'Work Request'
          ? 'workRequest'
          : 'workOrder'
      }`
    );
  };

  const { viewAllTasksEventCount, viewAllSitesEventCount, viewInspectionAndWorkRunCOunt } =
    useMemo(() => {
      let viewAllTasksEventCount = {};
      let viewAllSitesEventCount = 0;
      let viewInspectionAndWorkRunCOunt = 0;
      if (view === 'Check') {
        viewAllTasksEventCount = {
          notShowInProgressCount: false,
          allTasks: allCheckTasksCount,
          doneTasks: allDoneCheckTasksCount,
          chipText: `${allDoneCheckTasksCount}/${allCheckTasksCount}`,
          isOverDue: IsCheckOverDue,
          dot: !!IsCheckOverDue && allDoneCheckTasksCount !== allCheckTasksCount
        };
        viewAllSitesEventCount = allTasksData?.allSitesCountThatHaveChecks || 0;
      }

      if (view === 'Inspection') {
        viewAllTasksEventCount = {
          notShowInProgressCount: false,
          allTasks: allInspectionTasksCount,
          doneTasks: allDoneInspectionTasksCount,
          chipText: `${allDoneInspectionTasksCount}/${allInspectionTasksCount}`,
          isOverDue: IsInspectionOverDue,
          dot: !!IsInspectionOverDue && allDoneInspectionTasksCount !== allInspectionTasksCount
        };
        viewAllSitesEventCount = allTasksData?.allSitesCountThatHaveInspection || 0;
        viewInspectionAndWorkRunCOunt = allTasksData?.allRunsInspectionCount || 0;
      }

      if (view === 'Work Order') {
        viewAllTasksEventCount = {
          notShowInProgressCount: false,
          allTasks: allWOTasksCount,
          doneTasks: allDoneWOTasksCOunt,
          chipText: `${allDoneWOTasksCOunt}/${allWOTasksCount}`,
          isOverDue: isWOOverDue,
          dot: !!isWOOverDue && allDoneWOTasksCOunt !== allWOTasksCount,
          recordCount: newWorkOrderListCOunt
        };
        viewAllSitesEventCount = allTasksData?.allSitesCountThatHaveNewAndAssignedWOrkOrder || 0;
        viewInspectionAndWorkRunCOunt = allTasksData?.allWOrkOrderRecurringTemplateCount || 0;
      }

      if (view === 'Work Request') {
        viewAllTasksEventCount = {
          allTasks: newWorkRequestListCOunt,
          doneTasks: [],
          chipText: newWorkRequestListCOunt,
          isOverDue: false,
          dot: false,
          recordCount: newWorkRequestListCOunt,
          notShowInProgressCount: true
        };
        viewAllSitesEventCount = allTasksData?.allSitesCountThatHaveNewAndSyncedWorkRequest || 0;
      }
      if (view === 'HseEvent') {
        viewAllTasksEventCount = {
          allTasks: allHseEventsCount,
          doneTasks: [],
          chipText: allHseEventsCount,
          isOverDue: false,
          dot: false,
          recordCount: allHseEventsCount,
          notShowInProgressCount: true
        };
        viewAllSitesEventCount = allTasksData?.allSitesCountThatHaveNewAndSyncedHSEEvent || 0;
      }

      if (view === 'NewOrEditAsset') {
        viewAllTasksEventCount = {
          allTasks: editEquipmentCount,
          doneTasks: [],
          chipText: editEquipmentCount,
          isOverDue: false,
          dot: false,
          recordCount: editEquipmentCount,
          notShowInProgressCount: true
        };
        viewAllSitesEventCount = allTasksData?.editEquipmentViewBySitesCount || 0;
      }

      return { viewAllTasksEventCount, viewAllSitesEventCount, viewInspectionAndWorkRunCOunt };
    }, [view, allTasksData]);

  return (
    <div className="page-container">
      <AppBar />
      <div className="md:ml-24">
        <div className="relative overflow-x-hidden">
          {pageHeader()}

          {isLoading ? (
            <div style={{ backgroundColor: 'transparent', width: '100%' }}>
              <LoadingRound className={style.loaderHeight} />
            </div>
          ) : (
            <div className="page-listing">
              {!view ? (
                <>
                  {availableTaskType
                    ?.filter((x) => x?.allTasks > 0)
                    ?.map((ele, index) => {
                      return ele.title === 'All Tasks' && availableTaskType?.length === 2 ? (
                        <></>
                      ) : (
                        <PermissionRestrict key={index} roles={ele.permissions} fallback={fallback}>
                          <div className="task-type link-card" onClick={ele.onClick}>
                            <div className="task-type--left">
                              <Icon name={ele.icon} />
                              <div className="task-type--text">{ele.title}</div>
                            </div>
                            <div className="task-type--right">
                              <div style={{ display: 'flex', gap: '4px' }}>
                                {!ele?.notShowInProgressCount && (
                                  <ButtonStatus
                                    classes={`btn-status btn1 ${
                                      ele.doneTasks === 0 && 'btn-overdue'
                                    } ${
                                      ele.doneTasks > 0 &&
                                      ele.allTasks > ele.doneTasks &&
                                      'btn-progress'
                                    }  ${ele.doneTasks === ele.allTasks && 'btn-done'} `}
                                    Text={ele.chipText}
                                    dot={ele.dot}
                                  />
                                )}

                                {parseInt(ele?.recordCount) > 0 && (
                                  <ButtonStatus
                                    classes={`btn-status btn1 btn-count`}
                                    Text={ele?.recordCount}
                                  />
                                )}
                              </div>

                              <Icon name={'chevron-right'} width="10px" />
                            </div>
                          </div>
                        </PermissionRestrict>
                      );
                    })}
                </>
              ) : (
                <>
                  {/* view all tasks event start */}
                  <div className="task-type link-card" onClick={viewAllTasksEvent}>
                    <div className="task-type--left">
                      <div className="task-type--text">
                        View all{' '}
                        {view === 'HseEvent'
                          ? 'HSE event'
                          : view === 'NewOrEditAsset'
                          ? 'new or edited asset'
                          : view.toLowerCase()}
                        s
                      </div>
                    </div>
                    <div className="task-type--right">
                      <div className="task-type--right" style={{ gap: '4px' }}>
                        {!viewAllTasksEventCount?.notShowInProgressCount && (
                          <ButtonStatus
                            classes={`btn-status btn1 ${
                              viewAllTasksEventCount.doneTasks === 0 && 'btn-overdue'
                            } ${
                              viewAllTasksEventCount.doneTasks > 0 &&
                              viewAllTasksEventCount.allTasks > viewAllTasksEventCount.doneTasks &&
                              'btn-progress'
                            }  ${
                              viewAllTasksEventCount.doneTasks ===
                                viewAllTasksEventCount.allTasks && 'btn-done'
                            } `}
                            Text={viewAllTasksEventCount.chipText}
                            dot={viewAllTasksEventCount.dot}
                          />
                        )}

                        {parseInt(viewAllTasksEventCount?.recordCount) > 0 && (
                          <ButtonStatus
                            classes={`btn-status btn1 btn-count`}
                            Text={viewAllTasksEventCount?.recordCount}
                          />
                        )}
                      </div>

                      <Icon name={'chevron-right'} width="10px" />
                    </div>
                  </div>
                  {/* view all tasks event end */}

                  {/* view all sites event start */}

                  <div
                    className="task-type link-card"
                    onClick={() => {
                      clearAllFilterState();
                      navigate(
                        `/sites?type=${view === 'HseEvent' ? 'hseEvents' : view}&filterType=${
                          view === 'HseEvent'
                            ? 'hseEvents'
                            : view === 'Inspection'
                            ? 'inspection'
                            : view === 'Check'
                            ? 'check'
                            : view === 'Work Request'
                            ? 'workRequest'
                            : view === 'NewOrEditAsset'
                            ? view
                            : 'workOrder'
                        }`
                      );
                    }}
                  >
                    <div className="task-type--left">
                      <div className="task-type--text">View by site</div>
                    </div>
                    <div className="task-type--right">
                      {viewAllSitesEventCount > 0 && (
                        <ButtonStatus
                          classes={`btn-status btn1 btn-count`}
                          Text={viewAllSitesEventCount}
                        />
                      )}
                      <Icon name={'chevron-right'} width="10px" />
                    </div>
                  </div>
                  {/* view all sites event end */}

                  {['Inspection', 'Work Order'].includes(view) && (
                    <div
                      className="task-type link-card"
                      onClick={() => {
                        clearAllFilterState();
                        navigate(
                          `/${
                            view === 'Inspection' ? 'inspection-run' : 'recurring-template'
                          }?type=${view}`
                        );
                      }}
                    >
                      <div className="task-type--left">
                        <div className="task-type--text">
                          View by {view === 'Inspection' ? 'inspection run' : 'recurring template'}
                        </div>
                      </div>
                      <div className="task-type--right">
                        {viewInspectionAndWorkRunCOunt > 0 && (
                          <ButtonStatus
                            classes={`btn-status btn1 btn-count`}
                            Text={viewInspectionAndWorkRunCOunt}
                          />
                        )}

                        <Icon name={'chevron-right'} width="10px" />
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default memo(TaskType);
